import React from "react"
import { useTranslation } from "react-i18next"

import { useAuthentication } from "@/contexts"
import { useFormatPersonName } from "@/formatters"
import { userRoles } from "@/models/user-role"
import type { UserRoleType } from "@/models/user-role"
import { HStack } from "@/nzds/layout"

import { Box, Button, Typography } from "@mui/material"

const getUserRoleDisplayName = (userRoleId: UserRoleType): string => {
  const userRole = userRoles.find((r) => r.id === userRoleId)

  return userRole?.name ?? userRoleId
}

export const ImpersonateAlert: React.FC = () => {
  const { t } = useTranslation()
  const {
    activeRole,
    activeUser,
    authenticatedUser,
    isImpersonatingRole,
    setActiveRole,
    setActiveUser,
    setIsImpersonatingRole,
  } = useAuthentication()
  const { formatPersonName } = useFormatPersonName()

  const handleCancelClick = React.useCallback(() => {
    if (!authenticatedUser) {
      return
    }
    // Reset both user and role impersonation
    setActiveRole(authenticatedUser.role)
    setIsImpersonatingRole(false)
    setActiveUser(authenticatedUser)
  }, [authenticatedUser, setActiveRole, setActiveUser, setIsImpersonatingRole])

  // Show alert if impersonating a user
  const isImpersonatingUser =
    authenticatedUser && activeUser && authenticatedUser.id !== activeUser.id

  // Don't show if not impersonating
  if (!isImpersonatingUser && !isImpersonatingRole) {
    return null
  }

  // Determine what to display
  let displayText = ""

  if (isImpersonatingUser) {
    displayText = t("impersonateUser.bannerMessage", {
      userName: formatPersonName({
        firstName: activeUser.firstName,
        lastName: activeUser.lastName,
      }),
      ns: "common",
    })
  } else if (isImpersonatingRole && activeRole !== null) {
    displayText = t("impersonateRole.bannerMessage", {
      roleName: t(getUserRoleDisplayName(activeRole), { ns: "common" }),
      ns: "common",
    })
  }

  return (
    <Box
      sx={{
        backgroundColor: (theme) => theme.palette.primary.dark,
        borderRadius: "0 0 8px 8px",
        color: (theme) => theme.palette.primary.contrastText,
        left: "50%",
        position: "absolute",
        px: 2,
        py: 1,
        top: 0,
        transform: "translateX(-50%)",
        whiteSpace: "nowrap",
        zIndex: 1101,
      }}
    >
      <HStack sx={{ alignItems: "center", gap: 1 }}>
        <Typography variant="body2">{displayText}</Typography>
        <Button onClick={handleCancelClick} size="small" variant="contained">
          {t("button.cancel", { ns: "common" })}
        </Button>
      </HStack>
    </Box>
  )
}
